'use client';

import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import {
  ArrowUp,
  Check,
  ChevronRight,
  ChevronDown,
  CreditCard,
  LoaderCircle,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  Zap,
} from 'lucide-react';

import styles from './aiman-phone-preview.module.css';

type Beat = {
  kind:
    | 'agent'
    | 'visitor'
    | 'suggestions'
    | 'dates'
    | 'privacy'
    | 'terms'
    | 'action'
    | 'payment';
  text: string;
  duration: number;
};
const shortBeats: Beat[] = [
  { kind: 'visitor', text: 'Can you help me find a course?', duration: 3000 },
  {
    kind: 'agent',
    text: 'Of course. What would you like to study?',
    duration: 3000,
  },
  { kind: 'visitor', text: 'I’d like to study CPR.', duration: 3000 },
  {
    kind: 'agent',
    text: 'I’ll look up the next course dates for you.',
    duration: 3000,
  },
  {
    kind: 'agent',
    text: 'The next course dates are 18 September, 25 September and 2 October. Which date works for you?',
    duration: 9000,
  },
];
// Mirrors the configured NCVER notice in genx-apps/axcelerate/src/compliance.ts.
const privacyNotice =
  'Under the National Vocational Education and Training Regulator Act 2011, Your training provider is required to collect personal information from you. This information may be shared with the National Centre for Vocational Education Research (NCVER) and the relevant state/territory training authority.\n\nYour personal information (including the personal information contained on your enrolment form and your training activity data) may be used or disclosed by Your training provider for statistical, regulatory and research purposes. Your training provider may disclose your personal information to third parties including but not limited to: Commonwealth and state/territory agencies, NCVER, and organisations conducting student surveys.\n\nYou may receive an NCVER student survey which may be administered by a third party contractor on behalf of NCVER. You may opt out of the survey at the time of being contacted.\n\nFor more information about how your information is handled, please see the NCVER Privacy Notice at https://www.ncver.edu.au/privacy';
const enrolmentBeats: Beat[] = [
  { kind: 'suggestions', text: 'How can I help you today?', duration: 3000 },
  {
    kind: 'agent',
    text: 'Hi! I’m Aiman, your AI enrolment agent. I’ll help you find a course and enrol. What’s your name?',
    duration: 5500,
  },
  { kind: 'visitor', text: 'Alex Morgan', duration: 2500 },
  {
    kind: 'agent',
    text: 'Nice to meet you, Alex. What’s your email address?',
    duration: 3500,
  },
  { kind: 'visitor', text: 'alex.morgan@example.com', duration: 3000 },
  {
    kind: 'agent',
    text: 'Thanks, Alex. What would you like to study?',
    duration: 3500,
  },
  { kind: 'visitor', text: 'I’d like to enrol in CPR.', duration: 3000 },
  {
    kind: 'agent',
    text: 'I’ll look up the next CPR course dates for you.',
    duration: 3000,
  },
  { kind: 'action', text: 'Course dates lookup', duration: 2500 },
  {
    kind: 'dates',
    text: 'Here are the next available CPR dates. Which works for you?',
    duration: 5000,
  },
  {
    kind: 'privacy',
    text: 'Before we enrol you, please read the NCVER privacy notice.',
    duration: 15000,
  },
  {
    kind: 'terms',
    text: 'Please review your training provider’s terms and enrolment declaration.',
    duration: 7000,
  },
  {
    kind: 'agent',
    text: 'Thank you, Alex. You’ve accepted both. I’m submitting your CPR enrolment for 18 September at 9:00 am.',
    duration: 4500,
  },
  { kind: 'action', text: 'Submit enrolment', duration: 3000 },
  {
    kind: 'payment',
    text: 'Your enrolment is awaiting payment. Use the payment link below to complete your booking.',
    duration: 9000,
  },
];
const total = (beats: Beat[]) =>
  beats.reduce((sum, beat) => sum + beat.duration, 0);
function useWidgetScript() {
  useEffect(() => {
    if (document.querySelector('script[src*="leagen-chatbot.js"]')) return;
    const script = document.createElement('script');
    script.src = 'https://www.leagen.co/dist/leagen-chatbot.js';
    script.dataset.agentId = 'bcbb78a8-0682-4219-97fb-31438ba0d8c6';
    script.dataset.defaultAgentId = script.dataset.agentId;
    script.defer = true;
    document.body.appendChild(script);
  }, []);
}
export function AimanPhonePreview({
  showLauncher = true,
  dark = false,
  fullEnrolment = false,
  previewTime,
}: {
  showLauncher?: boolean;
  dark?: boolean;
  fullEnrolment?: boolean;
  previewTime?: number;
}) {
  const root = useRef<HTMLDivElement>(null),
    scroll = useRef<HTMLDivElement>(null),
    notice = useRef<HTMLDivElement>(null);
  const beats = fullEnrolment ? enrolmentBeats : shortBeats;
  const duration = total(beats);
  const [clock, setClock] = useState(duration - 1);
  const time = previewTime ?? clock;
  useEffect(() => {
    if (previewTime !== undefined) return;
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false,
      elapsed = 0,
      last = 0,
      raf = 0;
    const tick = (now: number) => {
      if (last) elapsed = (elapsed + Math.min(100, now - last)) % duration;
      last = now;
      setClock(elapsed);
      raf = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(raf);
      last = 0;
      if (media.matches) setClock(duration - 1);
      else if (visible && !document.hidden) raf = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    if (root.current) observer.observe(root.current);
    media.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      media.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [duration, previewTime]);
  let start = 0,
    phase = 0;
  for (let i = 0; i < beats.length; i++) {
    if (time < start + beats[i]!.duration) {
      phase = i;
      break;
    }
    start += beats[i]!.duration;
    phase = beats.length - 1;
  }
  const current = beats[phase]!;
  const elapsed = time - start;
  const typing = current.kind === 'visitor' && elapsed < current.duration - 650;
  const draft = typing ? current.text.slice(0, Math.floor(elapsed / 55)) : '';
  const tapping = elapsed > current.duration - 750;
  useEffect(() => {
    if (scroll.current) {
      scroll.current.scrollTop = scroll.current.scrollHeight;
      const top = scroll.current.scrollTop;
      for (const child of Array.from(scroll.current.children)) {
        const item = child as HTMLElement;
        item.style.opacity =
          item !== scroll.current.lastElementChild && item.offsetTop < top - 1
            ? '0'
            : '1';
      }
    }
    if (notice.current && current.kind === 'privacy') {
      const progress = Math.max(0, Math.min(1, (elapsed - 2500) / 9500));
      notice.current.scrollTop =
        progress * (notice.current.scrollHeight - notice.current.clientHeight);
    }
  }, [time, elapsed, current.kind]);
  const renderBeat = (beat: Beat, index: number) => {
    const active = index === phase;
    const finished = !active;
    if (beat.kind === 'visitor' && active && typing) return null;
    if (beat.kind === 'suggestions')
      return (
        <div key={index} className={styles.agent}>
          <Image
            src="/images/agents/aiman-avatar.jpg"
            width={22}
            height={22}
            alt=""
          />
          <div className={styles.messageContent}>
            <div className={styles.agentLabel}>
              Aiman <small>AI enrolment agent</small>
            </div>
            <span>{beat.text}</span>
            {finished && (
              <div className={styles.selectedReply}>
                Enrolments <Check size={12} />
              </div>
            )}
          </div>
        </div>
      );
    if (beat.kind === 'action')
      return (
        <div key={index} className={styles.action}>
          <Zap size={15} />
          <div>
            <small>Action · Student management system</small>
            <strong>{beat.text}</strong>
            <small>
              {active
                ? 'Connecting…'
                : beat.text === 'Submit enrolment'
                  ? 'Enrolment submitted'
                  : 'Dates retrieved'}
            </small>
          </div>
          {active ? (
            <LoaderCircle size={14} className={styles.spinner} />
          ) : (
            <Check size={14} />
          )}
        </div>
      );
    const isVisitor = beat.kind === 'visitor';
    return (
      <div key={index} className={isVisitor ? styles.visitor : styles.agent}>
        {!isVisitor && (
          <Image
            src="/images/agents/aiman-avatar.jpg"
            width={22}
            height={22}
            alt=""
          />
        )}
        <div className={styles.messageContent}>
          {!isVisitor && (
            <div className={styles.agentLabel}>
              Aiman <small>AI enrolment agent</small>
            </div>
          )}
          <span>
            {beat.kind === 'agent' && active
              ? beat.text.slice(0, Math.floor(elapsed / 25))
              : beat.text}
            {beat.kind === 'dates' && (
              <div className={styles.choiceList}>
                {[
                  '18 September · 9:00 am',
                  '25 September · 9:00 am',
                  '2 October · 1:00 pm',
                ].map((date, i) => (
                  <div
                    key={date}
                    className={styles.choice}
                    data-selected={i === 0 && (finished || tapping)}
                  >
                    {date}
                    {i === 0 && (finished || tapping) ? (
                      <Check size={12} />
                    ) : (
                      <ChevronRight size={12} />
                    )}{' '}
                    {active && tapping && i === 0 && (
                      <i className={styles.tap} />
                    )}
                  </div>
                ))}
              </div>
            )}
            {beat.kind === 'privacy' && (
              <div className={styles.notice}>
                <strong>
                  <ShieldCheck size={13} /> NCVER privacy notice
                </strong>
                {active ? (
                  <div ref={notice} className={styles.noticeBody}>
                    {privacyNotice}
                  </div>
                ) : (
                  <p>Privacy notice reviewed and agreed.</p>
                )}
                <div
                  className={styles.choice}
                  data-selected={finished || tapping}
                >
                  {finished ? 'Privacy notice agreed' : 'I agree'}
                  {(finished || tapping) && <Check size={12} />}{' '}
                  {active && tapping && <i className={styles.tap} />}
                </div>
              </div>
            )}
            {beat.kind === 'terms' && (
              <div className={styles.notice}>
                <strong>Terms & conditions</strong>
                <p>
                  {active
                    ? 'I confirm my details are correct and accept the training provider’s enrolment terms, including its fees, cancellation and refund policies.'
                    : 'Enrolment declaration accepted.'}
                </p>
                <div
                  className={styles.choice}
                  data-selected={finished || tapping}
                >
                  {finished ? 'Terms accepted' : 'I accept the terms'}
                  {(finished || tapping) && <Check size={12} />}{' '}
                  {active && tapping && <i className={styles.tap} />}
                </div>
              </div>
            )}
            {beat.kind === 'payment' && (
              <div className={styles.payment}>
                <small>CPR · Alex Morgan</small>
                <strong>18 September · 9:00 am</strong>
                <div className={styles.paymentLink}>
                  <CreditCard size={15} /> Pay securely{' '}
                  <ChevronRight size={14} />
                </div>
              </div>
            )}
          </span>
          {!isVisitor && (
            <div className={styles.feedback}>
              Just now <ThumbsUp size={12} />
              <ThumbsDown size={12} />
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <div
      ref={root}
      data-website-demo
      className={styles.root}
      data-beat={current.kind}
    >
      <div
        className={`${styles.phone} ${dark ? styles.darkPhone : ''}`}
        aria-label="Aiman course enquiry demonstration"
      >
        <div className={styles.camera} />
        <header>
          <Image className={styles.logo} src="/images/brand/leagen-standard-black.svg" alt="Leagen" width={110} height={30} />
        </header>
        <div ref={scroll} className={styles.messages}>
          {beats.slice(0, phase + 1).map(renderBeat)}
        </div>
        {current.kind === 'suggestions' && (
          <div className={styles.suggestions}>
            {[
              ['Enrolments', 'Find a course', 'How to enrol'],
              ['Fees / Finance', 'Course fees', 'Payment options'],
              ['FAQs', 'Course requirements', 'Get help'],
            ].map(([title, ...options], i) => (
              <details key={title} className={styles.suggestion} open={i === 0 && elapsed > 1000}>
                <summary>{title}<ChevronDown size={12} /></summary>
                <div className={styles.suggestionMenu}>
                  {options.map((option) => <div key={option}>{option}</div>)}
                </div>
              </details>
            ))}
          </div>
        )}
        <div className={styles.input}>
          <span>{draft || 'Message…'}</span>
          <ArrowUp
            size={18}
            data-sending={current.kind === 'visitor' && !typing}
          />
        </div>
      </div>
      {showLauncher && <AimanLauncher />}
    </div>
  );
}
export function AimanLauncher({ inline = false }: { inline?: boolean }) {
  useWidgetScript();
  return (
    <div className={inline ? styles.inlineLauncher : styles.launcher}>
      <leagen-launcher
        agent-id="bcbb78a8-0682-4219-97fb-31438ba0d8c6"
        style-variant="agent_avatar"
        img-url="/images/agents/aiman-avatar.jpg"
        primary-color="#008c44"
        effect="pulse"
        callout-text="Click me to chat"
        callout-position="below"
        callout-behavior="persist"
      />
    </div>
  );
}
